import React, { useState, useEffect } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import { emitFinanceEvent } from "../finance/financeDispatcher";
import PatientSearch from "../../doctor/PatientSearch";
import ProcedureHistory from "../../treatments/shared/ProcedureHistory";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface OrthoPatient {
  plan_id: number;
  patient_id: number;
  name: string;
  phone?: string;
  area?: string;
  status?: string;
  consultant_name?: string;
}

interface BillingData {
  plan_id: number;
  total_cost: number;
  paid: number;
  balance: number;

  consultant_name?: string;
  consultant_id?: number;
  total_consultant_cost?: number;
  consultant_paid?: number;
  consultant_balance?: number;

  history?: {
    id: number;
    session: number;
    date: string;
    amount: number;
    consultant_fee?: number;
    consultant_amount?: number;
    type: string
  }[];
}

interface Consultant {
  id: number;
  name: string;
}

export default function OrthoDashboard() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'history'>('dashboard');

  const [patients, setPatients] = useState<OrthoPatient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<OrthoPatient | null>(null);
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const consultantName = billingData?.consultant_name || "None";
  const hasConsultant = Boolean(billingData?.consultant_name);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newPatient, setNewPatient] = useState<{ name?: string, patient_id?: string } | null>(null);
  const [newTotalCost, setNewTotalCost] = useState("");

  // Consultant state for adding
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [selectedConsultantId, setSelectedConsultantId] = useState("");
  const [newConsultantCost, setNewConsultantCost] = useState("");

  const [payAmount, setPayAmount] = useState("");
  const [payConsultantAmount, setPayConsultantAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [payMode, setPayMode] = useState<"live" | "past">("live");
  const [payLoading, setPayLoading] = useState(false);

  const [editingCost, setEditingCost] = useState(false);
  const [editedCost, setEditedCost] = useState("");

  // Session Active State
  const [sessionActive, setSessionActive] = useState(false);

  useEffect(() => {
    loadPatients();
    loadConsultants();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      loadBilling(selectedPatient.patient_id);
    }
  }, [selectedPatient]);

  // Reload consultants when modal opens to ensure up-to-date list
  useEffect(() => {
    if (showAddModal) {
      loadConsultants();
    }
  }, [showAddModal]);

  const loadPatients = async () => {
    try {
      const data = await apiGet("/api/ortho/patients");
      setPatients((data as OrthoPatient[]) || []);
    } catch (e) {
      showToast({ message: "Failed to load ortho patients", type: "error" });
      setPatients([]);
    }
  };

  const loadConsultants = async () => {
    try {
      const data = await apiGet("/api/ortho/consultants");
      const list = (data as Consultant[]) || [];
      if (process.env.NODE_ENV === "development") {
        console.debug("Loaded consultants:", list.length);
      }
      setConsultants(list);
    } catch (e) {
      showToast({ message: "Failed to load consultants", type: "error" });
    }
  };

  const loadBilling = async (pid: number) => {
    try {
      const data = await apiGet(`/api/ortho/billing/${pid}`);
      setBillingData(data as BillingData);
    } catch (e) {
      showToast({ message: "Failed to load billing data", type: "error" });
    }
  };

  const handleStatusChange = async (planId: number, newStatus: string) => {
    setPatients((prev) =>
      prev.map((p) =>
        p.plan_id === planId ? { ...p, status: newStatus } : p
      )
    );
    try {
      await apiPut("/api/ortho/status", { plan_id: planId, status: newStatus });
      loadPatients();
    } catch (e) {
      showToast({ message: "Failed to update status", type: "error" });
      loadPatients();
    }
  };

  const handleAddPatient = async () => {
    if (!newPatient || !newPatient.patient_id) {
      showToast({ message: "Please select a valid patient first.", type: "warning" });
      return;
    }
    if (!newTotalCost) {
      showToast({ message: "Total cost is required.", type: "warning" });
      return;
    }

    try {
      await apiPost("/api/ortho/add-patient", {
        patient_id: newPatient.patient_id,
        total_cost: newTotalCost,
        consultant_id: selectedConsultantId ? Number(selectedConsultantId) : null,
        consultant_cost: newConsultantCost
      });

      showToast({ message: "Ortho plan added successfully.", type: "success" });
      setShowAddModal(false);
      setNewPatient(null);
      setNewTotalCost("");
      setSelectedConsultantId("");
      setNewConsultantCost("");
      loadPatients();
    } catch (e: unknown) {
      showToast({ message: `Failed to add patient: ${(e as Error)?.message || "Unknown error"}`, type: "error" });
    }
  };

  const handlePayment = async () => {
    if (!selectedPatient || (!payAmount && !payConsultantAmount) || payLoading) return;

    setPayLoading(true);
    try {
      const patientBalance = Number(billingData?.balance || 0);
      const consultantBalance = Number(billingData?.consultant_balance || 0);
      const patientPay = Number(payAmount || 0);
      const consultantPay = Number(payConsultantAmount || 0);
      if (patientPay > 0 && patientBalance > 0 && patientPay > patientBalance + 0.01) {
        showToast({ message: `Patient payment exceeds remaining balance (₹${patientBalance.toLocaleString("en-IN")}).`, type: "warning" });
        return;
      }
      if (consultantPay > 0 && consultantBalance > 0 && consultantPay > consultantBalance + 0.01) {
        showToast({ message: `Consultant payout exceeds remaining balance (₹${consultantBalance.toLocaleString("en-IN")}).`, type: "warning" });
        return;
      }

      await apiPost("/api/ortho/payment", {
        patient_id: selectedPatient.patient_id,
        amount: payAmount,
        consultant_amount: payConsultantAmount,
        is_past: payMode === "past",
        date: payMode === "past" ? payDate : null
      });

      if (payMode === "live" && Number(payAmount) > 0) {
        await emitFinanceEvent({
          source: "ORTHO",
          ref_id: String(selectedPatient.plan_id),
          debit: Number(payAmount),
          credit: 0,
          description: `Ortho payment – ${selectedPatient.name}`,
          meta: {
            patient_id: selectedPatient.patient_id,
            plan_id: selectedPatient.plan_id,
            payment_mode: "Cash"
          }
        });
      }

      showToast({ message: "Payment recorded successfully", type: "success" });
      setPayAmount("");
      setPayConsultantAmount("");
      setPayDate("");
      loadBilling(selectedPatient.patient_id);
    } catch (e: unknown) {
      showToast({ message: `Failed to process payment: ${(e as Error)?.message || "Unknown error"}`, type: "error" });
    } finally {
      setPayLoading(false);
    }
  };

  const handleDeleteHistory = async (instId: number) => {
    if (!selectedPatient) return;
    try {
      await apiDelete(`/api/ortho/payment/${instId}`);
      showToast({ message: "Payment deleted successfully", type: "success" });
      loadBilling(selectedPatient.patient_id);
    } catch (e: unknown) {
      showToast({ message: `Failed to delete payment: ${(e as Error)?.message || "Unknown error"}`, type: "error" });
    }
  };

  const handleCostSave = async () => {
    if (!selectedPatient || !billingData) return;
    try {
      await apiPut("/api/ortho/update-cost", {
        plan_id: billingData.plan_id,
        total_cost: editedCost
      });

      showToast({ message: "Cost updated successfully", type: "success" });
      setEditingCost(false);
      loadBilling(selectedPatient.patient_id);
    } catch (e: unknown) {
      showToast({ message: `Failed to update cost: ${(e as Error)?.message || "Unknown error"}`, type: "error" });
    }
  };

  const getStatusColor = (status?: string) => {
    if (status === "Informed") return "#16a34a";
    if (status === "No Answer") return "#d97706";
    return "#dc2626";
  };

  const progress = billingData
    ? Math.min(100, (billingData.paid / billingData.total_cost) * 100)
    : 0;

  const inputStyle = { padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' as const, outline: 'none' };

  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', gap: '20px', minHeight: 'calc(100vh - 120px)' }}>
      <div className="mod-window-header" style={{ paddingBottom: 0, borderBottom: 'none', marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, fontSize: '1.5rem', color: '#0f172a' }}>😬 Orthodontics Dashboard</h2>
          <p className="mod-text-muted" style={{ margin: '4px 0 0 0', fontSize: '0.9rem' }}>Manage long-term ortho plans, payments, and consultants.</p>
        </div>
        <div className="mod-sub-nav">
          <button className={`mod-sub-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>Active Plans</button>
          <button className={`mod-sub-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>Clinical History</button>
        </div>
      </div>

      {activeTab === 'history' ? (
        <div className="mod-panel-fade-in"><ProcedureHistory procedureType="Ortho" title="Orthodontic History" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(400px, 2fr)', gap: '24px', flex: 1, alignItems: 'start' }}>
          
          {/* Left Sidebar: Patient List */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '72vh', boxShadow: 'var(--mod-shadow-sm)', overflow: 'hidden' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>Active Patients</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Button variant={sessionActive ? "secondary" : "ghost"} size="sm" onClick={() => setSessionActive(!sessionActive)}>
                  {sessionActive ? "📡 Session On" : "📡 Nav Off"}
                </Button>
                <Button variant="primary" size="sm" onClick={() => { setNewPatient(null); setShowAddModal(true); }}>+ Add</Button>
              </div>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {patients.length === 0 ? (
                 <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '0.9rem' }}>No active patients found.</div>
              ) : (
                patients.map(p => (
                  <div key={p.plan_id} onClick={() => setSelectedPatient(p)} style={{ padding: '12px', borderRadius: '8px', cursor: 'pointer', border: selectedPatient?.plan_id === p.plan_id ? '1px solid #0ea5e9' : '1px solid #e2e8f0', background: selectedPatient?.plan_id === p.plan_id ? '#f0f9ff' : '#fff', position: 'relative' }}>
                    <div style={{ fontWeight: selectedPatient?.plan_id === p.plan_id ? 700 : 500, color: '#0f172a' }}>{p.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                      {p.consultant_name ? `👨‍⚕️ ${p.consultant_name}` : `📞 ${p.phone}`}
                    </div>
                    {sessionActive && (
                      <div style={{ display: 'flex', gap: '6px', marginTop: '12px' }}>
                        <button title="Not Informed" onClick={(e) => { e.stopPropagation(); handleStatusChange(p.plan_id, "Not Informed"); }} style={{ width: '16px', height: '16px', borderRadius: '50%', background: p.status === "Not Informed" ? '#dc2626' : '#fca5a5', border: p.status === "Not Informed" ? '2px solid #7f1d1d' : 'none', cursor: 'pointer', padding: 0 }} />
                        <button title="No Answer" onClick={(e) => { e.stopPropagation(); handleStatusChange(p.plan_id, "No Answer"); }} style={{ width: '16px', height: '16px', borderRadius: '50%', background: p.status === "No Answer" ? '#d97706' : '#fcd34d', border: p.status === "No Answer" ? '2px solid #78350f' : 'none', cursor: 'pointer', padding: 0 }} />
                        <button title="Informed" onClick={(e) => { e.stopPropagation(); handleStatusChange(p.plan_id, "Informed"); }} style={{ width: '16px', height: '16px', borderRadius: '50%', background: p.status === "Informed" ? '#16a34a' : '#86efac', border: p.status === "Informed" ? '2px solid #14532d' : 'none', cursor: 'pointer', padding: 0 }} />
                      </div>
                    )}
                    <div style={{ position: 'absolute', right: '12px', top: '12px', width: '10px', height: '10px', borderRadius: '50%', background: getStatusColor(p.status) }} title={p.status || "Unknown"} />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel: Billing & Ledger */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto', maxHeight: '72vh', paddingRight: '8px' }}>
            {!selectedPatient ? (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                Select a patient from the sidebar to manage their ortho plan.
              </div>
            ) : (
              <>
                {/* Details Card */}
                <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#0f172a' }}>{selectedPatient.name}</h2>
                      <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '0.85rem', color: '#64748b' }}>
                        <span>📞 {selectedPatient.phone || "No Phone"}</span>
                        <span>📍 {selectedPatient.area || "No Area"}</span>
                        {hasConsultant && <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>👨‍⚕️ {consultantName}</span>}
                      </div>
                    </div>
                    <div>
                      <select
                        style={{ ...inputStyle, background: '#f8fafc', fontWeight: 600, color: getStatusColor(selectedPatient.status) }}
                        value={selectedPatient.status || "Not Informed"}
                        onChange={(e) => handleStatusChange(selectedPatient.plan_id, e.target.value)}
                      >
                        <option value="Not Informed">🔴 Not Informed</option>
                        <option value="No Answer">🟠 No Answer</option>
                        <option value="Informed">🟢 Informed</option>
                      </select>
                    </div>
                  </div>

                  {/* Top-line Metrics */}
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                    <div style={{ flex: 1, background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Patient Financials</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#475569' }}>Total</span>
                        <span style={{ fontWeight: 600 }}>
                          {!editingCost ? (
                            <span style={{ cursor: 'pointer', borderBottom: '1px dashed #94a3b8' }} onClick={() => { setEditedCost(String(billingData?.total_cost ?? "")); setEditingCost(true); }}>
                              ₹{Number(billingData?.total_cost || 0).toLocaleString("en-IN")} ✏️
                            </span>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <input type="number" style={{ width: '80px', padding: '2px 4px' }} value={editedCost} onChange={e => setEditedCost(e.target.value)} />
                              <button onClick={handleCostSave}>✓</button>
                              <button onClick={() => setEditingCost(false)}>&times;</button>
                            </div>
                          )}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.9rem', color: '#475569' }}>Paid</span>
                        <span style={{ fontWeight: 600, color: '#16a34a' }}>₹{Number(billingData?.paid || 0).toLocaleString("en-IN")}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px solid #cbd5e1' }}>
                        <span style={{ fontSize: '0.9rem', color: '#475569', fontWeight: 600 }}>Balance</span>
                        <span style={{ fontWeight: 800, color: '#dc2626' }}>₹{Number(billingData?.balance || 0).toLocaleString("en-IN")}</span>
                      </div>
                    </div>

                    {hasConsultant && (
                      <div style={{ flex: 1, background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Consultant Financials</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '0.9rem', color: '#475569' }}>Fee</span>
                          <span style={{ fontWeight: 600 }}>₹{Number(billingData?.total_consultant_cost || 0).toLocaleString("en-IN")}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '0.9rem', color: '#475569' }}>Paid</span>
                          <span style={{ fontWeight: 600, color: '#16a34a' }}>₹{Number(billingData?.consultant_paid || 0).toLocaleString("en-IN")}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px', borderTop: '1px solid #cbd5e1' }}>
                          <span style={{ fontSize: '0.9rem', color: '#475569', fontWeight: 600 }}>Balance</span>
                          <span style={{ fontWeight: 800, color: '#dc2626' }}>₹{Number(billingData?.consultant_balance || 0).toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div style={{ background: '#f1f5f9', height: '12px', borderRadius: '6px', overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: progress >= 100 ? '#10b981' : '#3b82f6', transition: 'width 0.3s ease' }} />
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                    {progress.toFixed(1)}% Completed {(billingData?.balance ?? 0) <= 0 && <span style={{ color: '#10b981', fontWeight: 700, marginLeft: '8px' }}>✓ Paid in Full</span>}
                  </div>
                </div>

                {/* Entry Form Card */}
                <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' }}>
                   <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>Record Payment Session</h3>
                   
                   <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" checked={payMode === "live"} onChange={() => setPayMode("live")} /> <span style={{ fontSize: '0.9rem', color: '#0f172a' }}>Live (Today)</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" checked={payMode === "past"} onChange={() => setPayMode("past")} /> <span style={{ fontSize: '0.9rem', color: '#0f172a' }}>Past Entry</span>
                      </label>
                   </div>

                   <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                     {payMode === "past" && (
                       <div style={{ flex: 1, minWidth: '150px' }}>
                         <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>Date</label>
                         <input type="date" style={inputStyle} value={payDate} onChange={e => setPayDate(e.target.value)} />
                       </div>
                     )}
                     <div style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>Patient Paid</label>
                        <input type="number" min={0} style={inputStyle} placeholder="₹ Amount" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
                     </div>
                     {hasConsultant && (
                       <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>Consultant Paid</label>
                          <input type="number" min={0} style={inputStyle} placeholder="₹ Amount" value={payConsultantAmount} onChange={e => setPayConsultantAmount(e.target.value)} />
                       </div>
                     )}
                     <div style={{ flexShrink: 0 }}>
                       <Button variant="primary" onClick={handlePayment} isLoading={payLoading}>Save Session</Button>
                     </div>
                   </div>
                   {payMode === "live" && <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', marginTop: '12px' }}>* Live patient payments are logged to Clinic Revenue automatically.</div>}
                </div>

                {/* Ledger Table Base */}
                <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: 'var(--mod-shadow-sm)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem' }}>Session Ledger</h3>
                  {billingData?.history && billingData.history.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>Session</th>
                            <th style={{ padding: '10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>Date</th>
                            <th style={{ padding: '10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Client Paid</th>
                            {hasConsultant && <th style={{ padding: '10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Cons. Payout</th>}
                            <th style={{ padding: '10px', textAlign: 'center', color: '#475569', fontWeight: 600 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {billingData.history.map((h, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px', color: '#0f172a' }}>{h.session}</td>
                              <td style={{ padding: '10px', color: '#64748b' }}>{new Date(h.date).toLocaleDateString("en-IN")}</td>
                              <td style={{ padding: '10px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>₹{Number(h.amount || 0).toLocaleString("en-IN")}</td>
                              {hasConsultant && <td style={{ padding: '10px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>₹{Number(h.consultant_amount || 0).toLocaleString("en-IN")}</td>}
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                  <span style={{ fontSize: '0.75rem', background: String(h.type || "").toLowerCase().includes("live") ? '#dcfce7' : '#f1f5f9', color: String(h.type || "").toLowerCase().includes("live") ? '#166534' : '#475569', padding: '2px 6px', borderRadius: '4px' }}>
                                    {h.type}
                                  </span>
                                  {String(h.type || "").toLowerCase().includes("past") && (
                                    <Button variant="outline" size="sm" onClick={() => handleDeleteHistory(h.id)} style={{ color: '#dc2626', borderColor: '#fca5a5', padding: '4px 8px' }}>
                                      🗑
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>No payment sessions recorded yet.</div>
                  )}
                </div>

              </>
            )}
          </div>
        </div>
      )}

      {/* Modern Blurred Modal for Add Patient */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowAddModal(false)}>
          <div style={{ width: '500px', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>New Ortho Plan</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px', display: 'block' }}>1. Select Patient</label>
                <PatientSearch onSelect={(p) => setNewPatient({ name: p.name, patient_id: String(p.patient_id) })} />
                {newPatient && (
                  <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '8px', fontSize: '0.9rem' }}>
                    Patient: <strong style={{ color: '#0f172a' }}>{newPatient.name}</strong>
                  </div>
                )}
              </div>
              
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px', display: 'block' }}>2. Total Plan Cost (₹)</label>
                <input type="number" min={0} style={inputStyle} placeholder="Total Agreed Amount" value={newTotalCost} onChange={e => setNewTotalCost(e.target.value)} />
              </div>

              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px', display: 'block' }}>3. Assign Consultant (Optional)</label>
                <select style={{ ...inputStyle, marginBottom: '12px' }} value={selectedConsultantId} onChange={e => setSelectedConsultantId(e.target.value)}>
                  <option value="">-- Clinic Handled --</option>
                  {consultants.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                
                {selectedConsultantId && (
                  <div>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '4px', display: 'block' }}>Consultant Agreed Fee (₹)</label>
                    <input type="number" min={0} style={inputStyle} placeholder="Consultant Fee" value={newConsultantCost} onChange={e => setNewConsultantCost(e.target.value)} />
                  </div>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button variant="ghost" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAddPatient} disabled={!newPatient}>Add Ortho Plan</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
